import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Home as HomeIcon, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine } from "../components/Reveal";

const IMG = "https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxiaWdfa2l0Y2hlbl9yZXNpZGVudGlhbHw&q=85";
const IMG_DEEP = "https://images.pexels.com/photos/4099470/pexels-photo-4099470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_MOVE = "https://images.unsplash.com/photo-1692133220749-1c55bb918ad8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxlbXB0eV9yZXNhdXJhbnR8&q=85";

const PLANS = [
  { title: "Standard & Recurring", text: "Weekly, bi-weekly, or monthly visits that keep your home consistently clean - kitchens, bathrooms, dusting, vacuuming, and mopping on every visit.", tag: "Most popular" },
  { title: "Deep Cleaning", text: "A top-to-bottom reset: baseboards, fixtures, buildup in kitchens and bathrooms, and the spots regular cleaning never reaches. The right starting point for a new recurring plan.", tag: "Great first visit" },
  { title: "Move-In / Move-Out", text: "A complete empty-home clean - inside appliances and cabinets, bathrooms, floors, and edges - timed around your move, walkthrough, or listing photos.", tag: "One-time" },
];

const ROOMS = [
  { name: "Kitchen", items: "Counters, sink, appliance exteriors, cabinet fronts, floors" },
  { name: "Bathrooms", items: "Toilet, shower & tub, tile, vanity, mirrors, floors" },
  { name: "Living areas", items: "Dusting, high-touch surfaces, vacuuming, mopping" },
  { name: "Bedrooms", items: "Dusting, floors, beds made or linens changed on request" },
];

export default function Residential() {
  const [svc, setSvc] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setSvc(r.data.find((s) => s.category === "residential") || null)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Residential & House Cleaning | St. Louis & Chesterfield, MO | Glow Green"
        description="Residential cleaning in St. Louis and Chesterfield, MO: regular house cleaning, deep cleaning, and move-in/move-out cleaning with customized plans. Request a free quote."
      />

      <section className="bg-forest grain relative pt-32 lg:pt-44 pb-20 lg:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="uppercase text-xs font-semibold tracking-[0.25em] text-leaf font-mono">Residential Cleaning</p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              <MaskedLine delay={0.2}>Come home to</MaskedLine>
              <MaskedLine delay={0.35} className="text-leaf">the glow.</MaskedLine>
            </h1>
            <p className="mt-6 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
              Regular, deep, and move-in/move-out cleaning for houses and apartments across St. Louis and Chesterfield - a consistent checklist, a consistent result, a plan shaped around your home.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="residential-quote-button" className="inline-flex items-center gap-2 rounded-full bg-leaf px-7 py-4 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03]">
                Get a Residential Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl clipped-frame aspect-[4/3]">
              <img src={IMG} alt="Pristine residential kitchen with white marble island and spotless cabinets" className="w-full h-full object-cover" fetchPriority="high" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">Cleaning plans</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-forest max-w-2xl">The right clean for where your home is today.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {PLANS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="rounded-2xl bg-white border border-forest/10 p-6 lg:p-8 h-full hover:border-leaf/40 hover:shadow-lg transition-all">
                  <span className="rounded-full bg-mint px-3 py-1 text-xs font-semibold text-forest">{p.tag}</span>
                  <h3 className="mt-4 font-display text-xl font-bold text-forest">{p.title}</h3>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">Room by room</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight text-forest">One checklist. Every room. Every visit.</h2>
            <div className="mt-8 space-y-4">
              {ROOMS.map((r) => (
                <div key={r.name} className="flex gap-4 items-start rounded-xl border border-forest/10 p-4">
                  <span className="w-9 h-9 rounded-full bg-mint flex items-center justify-center flex-shrink-0">
                    <HomeIcon className="w-4 h-4 text-leaf-hover" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-forest text-sm">{r.name}</h3>
                    <p className="text-sm text-slate-600 mt-0.5">{r.items}</p>
                  </div>
                </div>
              ))}
            </div>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {(svc?.items || []).map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-leaf flex-shrink-0 mt-0.5" />{item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="space-y-5">
            <div className="overflow-hidden rounded-2xl clipped-frame aspect-[4/3]">
              <img src={IMG_DEEP} alt="Professional cleaner detail-wiping kitchen counters during a deep clean" loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-2xl clipped-frame aspect-[4/3]">
              <img src={IMG_MOVE} alt="Immaculate empty room with gleaming hardwood floor ready for move-in" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-forest grain relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center relative z-10">
          <Sparkles className="w-8 h-8 text-leaf mx-auto" />
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Your home, on your schedule.</h2>
          <p className="mt-4 text-white/70">Customized plans, honest pricing, and a team that treats your home like their own.</p>
          <Link to="/contact" data-testid="residential-bottom-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-leaf px-8 py-4 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03]">
            Request a Free Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
